import React, { Component } from 'react'
import moment from 'moment'
import isEmpty from 'lodash/isEmpty'
import keyBy from 'lodash/keyBy'
import merge from 'lodash/merge'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { isJustGiving } from '../../../lib/environment'
import mapTemplate from '../../../lib/mapTemplate'
import { clearSession, fetchSessionPage, fetchSessionUser, selectPage, selectUser } from '../../../store/session'
import { selectAPIParams, selectButtonDefaults, selectLocalisation } from '../../../store/site'
import withStyles from 'constructicon/with-styles'
import styles from './styles'
import * as validators from 'constructicon/lib/validators'

import Button from '../../ui/Button'
import Content from '../../ui/Content'
import CreatePageForm from 'supporticon/components/create-page-form'
import FormState from './FormState'
import Heading from 'constructicon/heading'
import LoginForm from 'supporticon/components/login-form'
import Modal from '../../ui/FlatModal'
import ProviderOauthButton from 'supporticon/components/provider-oauth-button'
import ResetPassword from 'supporticon/components/reset-password-form'
import RichText from '../../ui/RichText'
import Section from '../../ui/Section'
import SignupForm from 'supporticon/components/signup-form'
import SingleSignOnLink from 'supporticon/components/single-sign-on-link'
import Success from '../../ui/Success'
import TeamRegistration from './TeamRegistration'

class RegisterForm extends Component {
  constructor (props) {
    super(props)
    this.handleAuthSuccess = this.handleAuthSuccess.bind(this)
    this.handleCreatePageSuccess = this.handleCreatePageSuccess.bind(this)
    this.handleLogout = this.handleLogout.bind(this)
    this.renderAuthForm = this.renderAuthForm.bind(this)
    this.renderAuthProviders = this.renderAuthProviders.bind(this)
    this.renderCreatePageForm = this.renderCreatePageForm.bind(this)
    this.renderSuccess = this.renderSuccess.bind(this)

    this.state = {
      view: 'signup',
      modal: !!props.page
    }
  }

  handleAuthSuccess (data) {
    const token = data.token || data.access_token
    this.props.fetchSessionUser({ token })
  }

  handleCreatePageSuccess (page) {
    this.props.fetchSessionPage(page)
      .then(() => this.setState({ modal: true }))
  }

  handleLogout (data) {
    this.setState({ modal: false, view: 'signup' })
    this.props.clearSession()
  }

  renderAuthProviders (method) {
    const {
      button,
      classNames,
      clientId,
      facebook,
      mapmyfitness,
      oauthMethod,
      redirectUri,
      strava,
      styles
    } = this.props

    if (isJustGiving()) { return null }

    const providers = [
      facebook === 'Yes' && 'facebook',
      mapmyfitness === 'Yes' && 'mapmyfitness',
      strava === 'Yes' && 'strava'
    ].filter((key) => key)

    return providers.length > 0 && (
      <div className={classNames.providers}>
        {providers.map((provider, i) => (
          <ProviderOauthButton
            block
            key={provider}
            provider={provider}
            label={`${method} with ${provider}`}
            clientId={clientId || process.env.EDH_CLIENT_ID}
            redirectUri={redirectUri || process.env.EDH_REDIRECT_URI}
            onSuccess={this.handleAuthSuccess}
            styles={styles.provider}
            useLocalStorage={oauthMethod === 'Advanced'}
            {...button}
          />
        ))}
        <Heading tag='h3' size={0}>Sign up with Email</Heading>
      </div>
    )
  }

  renderAuthForm () {
    const {
      clientId,
      country,
      foreground,
      resetPasswordLabel,
      loginLabel,
      signupLabel,
      styles
    } = this.props

    const { modal } = this.state

    const platform = isJustGiving() ? 'JustGiving' : 'everydayhero'
    const platformWithArticle = isJustGiving() ? 'a JustGiving' : 'an everydayhero'
    const countryCode = country === 'gb' ? 'uk' : country

    switch (this.state.view) {
      case 'reset-password':
        return (
          <FormState
            state='reset-password'
            foreground={foreground}
            links={[{ label: 'Back to login', onClick: () => this.setState({ view: 'login' }) }]}>
            <Heading styles={styles.heading}>Reset your {platform} password</Heading>
            <ResetPassword
              clientId={clientId || process.env.EDH_CLIENT_ID}
              formComponent={{ styles: styles.form }}
              onSuccess={(result) => this.setState({ modal: true })}
              submit={resetPasswordLabel || 'Send reset password instructions'}
            />
            <Modal
              contentLabel='Email sent!'
              isOpen={modal}
              onRequestClose={() => this.setState({ modal: false, view: 'login' })}
              spacing={1.5}>
              <Success title='Email sent!'>
                <RichText children={[
                  { type: 'paragraph', text: `If ${platformWithArticle} account exists with the email address entered, you will receive an email within the next few minutes.` },
                  { type: 'paragraph', text: 'It contains instructions for changing your password.' }
                ]} />
              </Success>
            </Modal>
          </FormState>
        )
      case 'login':
        return (
          <FormState
            state='login'
            foreground={foreground}
            links={[
              { label: `Forgot your ${platform} password?`, onClick: () => this.setState({ view: 'reset-password' }) },
              { label: `Need ${platformWithArticle} account?`, onClick: () => this.setState({ view: 'signup' }) }
            ]}>
            <Heading styles={styles.heading}>Login to {platform}</Heading>
            {this.renderAuthProviders('Login')}
            <LoginForm
              clientId={clientId || process.env.EDH_CLIENT_ID}
              country={countryCode}
              formComponent={{ styles: styles.form }}
              onSuccess={this.handleAuthSuccess}
              submit={loginLabel || 'Log in'}
            />
          </FormState>
        )
      default:
        return (
          <FormState
            state='signup'
            foreground={foreground}
            links={[{ label: `Already have ${platformWithArticle} account?`, onClick: () => this.setState({ view: 'login' }) }]}>
            <Heading styles={styles.heading}>Create {platformWithArticle} account</Heading>
            {this.renderAuthProviders('Sign up')}
            <SignupForm
              clientId={clientId || process.env.EDH_CLIENT_ID}
              country={countryCode}
              formComponent={{ styles: styles.form }}
              onSuccess={this.handleAuthSuccess}
              submit={signupLabel || 'Sign up (Step 1 of 2)'}
            />
          </FormState>
        )
    }
  }

  renderCreatePageForm () {
    const {
      addressCountry,
      campaignId,
      charityId,
      clientId,
      createPageLabel,
      createPageTitle,
      eventId,
      foreground,
      gdprLabel,
      includeCharitySearch,
      items = [],
      pageEventDateLabel,
      pageNameLabel,
      pageTargetLabel,
      showAddress,
      showEventDate,
      showFitnessGoal,
      showName,
      showTarget,
      styles,
      terms,
      uom,
      user = {}
    } = this.props

    const fields = merge({}, !isJustGiving() && {
      birthday: {
        showSelects: true,
        placeholder: null,
        order: 2,
        max: moment().subtract(1, 'day').format(moment.HTML5_FMT.DATE),
        validators: [
          validators.required('Please enter your date of birth'),
          val => !!val && moment(val).isAfter(moment().subtract(1, 'day').startOf('day')) && 'Please select a date in the past'
        ]
      }
    },
    showName === 'Yes' && {
      name: {
        label: pageNameLabel || 'Fundraising page name',
        type: 'text',
        placeholder: `${user.firstName}'s page`,
        required: true,
        order: 1,
        validators: [
          validators.required('Please enter a page name')
        ]
      }
    },
    showTarget === 'Yes' && {
      target: {
        label: pageTargetLabel || 'Fundraising target',
        type: 'number',
        required: true,
        order: 1,
        validators: [
          validators.required('Please enter a fundraising target')
        ],
        styles: styles.target
      }
    },
    showEventDate === 'Yes' && {
      [isJustGiving() ? 'eventDate' : 'campaignDate']: {
        label: pageEventDateLabel || 'Event date',
        type: 'date',
        required: true,
        order: 2,
        validators: [
          validators.required('Please enter an event date')
        ]
      }
    },
    showFitnessGoal === 'Yes' && {
      fitnessGoal: {
        label: `Distance goal (in ${uom})`,
        type: 'number',
        required: true,
        order: 2,
        validators: [
          validators.required('Please enter a fitness goal')
        ],
        styles: styles.fitnessGoal
      }
    },
    !isEmpty(gdprLabel) && {
      directMarketingConsent: {
        label: <RichText styles={styles.termsLabel}>{gdprLabel}</RichText>,
        type: 'checkbox',
        order: 3,
        styles: styles.termsCheckbox
      }
    },
    !isEmpty(terms) && {
      terms: {
        label: <RichText styles={styles.termsLabel}>{terms}</RichText>,
        type: 'checkbox',
        required: true,
        order: 4,
        validators: [
          validators.required('You must agree to the terms & conditions.')
        ],
        styles: styles.termsCheckbox
      }
    }, keyBy(items.map((field, index) => ({
      name: field.name || `custom_field_${index}`,
      label: <RichText styles={styles.label}>{field.label}</RichText>,
      type: field.type,
      required: field.required === 'Yes',
      order: field.order,
      options: field.options && field.options.split(',').map(option => ({ label: option.trim(), value: option.trim() })),
      validators: field.required === 'Yes' && [
        validators.required(field.validationLabel || 'This field is required')
      ]
    })), 'name'))

    return (
      <FormState
        state='create-page'
        foreground={foreground}
        links={[{ label: 'Log out', onClick: this.handleLogout }]}>
        <Heading styles={styles.heading}>{mapTemplate(createPageTitle || 'Create Fundraising Page', { user })}</Heading>
        <CreatePageForm
          campaignId={campaignId || this.props.campaign}
          charityId={charityId || this.props.charity}
          clientId={clientId || process.env.EDH_CLIENT_ID}
          country={addressCountry}
          eventId={eventId || this.props.event}
          fields={fields}
          formComponent={{ styles: styles.createPageForm }}
          includeAddress={showAddress === 'Yes'}
          includeCharitySearch={includeCharitySearch === 'Yes'}
          includePhone={!user.phone}
          onSuccess={this.handleCreatePageSuccess}
          submit={createPageLabel || 'Create Page (Step 2 of 2)'}
          token={user.token}
        />
      </FormState>
    )
  }

  renderSuccess () {
    const {
      button,
      campaignId,
      charityId,
      foreground,
      includeTeam,
      page,
      styles,
      successButtonLabel,
      successCopy,
      successTitle,
      user
    } = this.props

    return (
      <Success id='register-success' title={mapTemplate(successTitle || 'Congratulations <%= userName %>!', { user, page })}>
        {successCopy.length > 0 && (
          <RichText styles={styles.copy}>
            {successCopy.map((item) => ({
              ...item,
              text: mapTemplate(item.text, { user, page })
            }))}
          </RichText>
        )}
        <SingleSignOnLink
          label={mapTemplate(successButtonLabel || 'Visit your page', { user, page })}
          token={user.token}
          url={page.url}
          size={0.5}
          styles={styles.button}
          {...button}
        />
        {includeTeam === 'Yes' && (
          <TeamRegistration
            user={user}
            page={page}
            campaign={campaignId || this.props.campaign}
            charity={charityId || this.props.charity}
          />
        )}
        <Button
          background='transparent'
          foreground={foreground}
          onClick={this.handleLogout}
          styles={styles.link}
          tag='button'>
          Log out
        </Button>
      </Success>
    )
  }

  render () {
    const {
      alignment,
      background,
      backgroundImage,
      copy,
      copyAlignment,
      foreground,
      layout,
      page,
      size,
      spacing,
      subtitle,
      title,
      titleForeground,
      user
    } = this.props

    return (
      <Section
        alignment={alignment}
        backgroundImage={backgroundImage}
        background={background}
        foreground={foreground}
        size={size}
        spacing={spacing}>

        <Content
          copy={copy}
          copyAlignment={copyAlignment}
          layout={layout}
          subtitle={subtitle}
          title={title}
          titleForeground={titleForeground}>

          {page ? this.renderSuccess() : user
            ? this.renderCreatePageForm()
            : this.renderAuthForm()
          }
        </Content>
      </Section>
    )
  }
}

RegisterForm.defaultProps = {
  clientId: process.env.EDH_CLIENT_ID,
  redirectUri: process.env.EDH_REDIRECT_URI,
  includeTeam: 'Yes'
}

const mapState = (store) => {
  const { apiParams } = selectAPIParams(store)

  return {
    ...apiParams,
    ...selectLocalisation(store),
    button: selectButtonDefaults(store),
    user: selectUser(store),
    page: selectPage(store)
  }
}

const mapDispatch = { clearSession, fetchSessionUser, fetchSessionPage }

export default compose(
  connect(mapState, mapDispatch),
  withStyles(styles)
)(RegisterForm)



// WEBPACK FOOTER //
// ./components/slices/Registration/index.js